<template>
    <Card :pageTitle="`Quantity Adjustment #${$route.params.id}`">
        <AllowPermission :permission="PERMISSION_CODES.CMS_VIEW_ADJUSTMENT">
            <BackNavigateBtn title="Back to Qty Adjustments" to="/inventory-adjustments/quantity" />
            <div v-if="loading" class="text-center py-5">
                <div class="spinner-border text-primary" role="status">
                    <span class="sr-only">Loading...</span>
                </div>
                <p class="mt-2">Loading adjustment details...</p>
            </div>
            <div v-else-if="qtyAdjustmentDetails">
                <div class="adjustment-header mb-4">
                    <div class="d-flex justify-content-between flex-wrap">
                        <div>
                            <h3 class="mb-1">{{ qtyAdjustmentDetails.name || $route.params.id }}</h3>
                            <p class="text-secondary mb-0">Adjusted on {{ qtyAdjustmentDetails.updatedAtFormatted1 }}</p>
                            <p class="text-secondary mb-0" v-if="qtyAdjustmentDetails.createdBy">Adjusted by: {{ qtyAdjustmentDetails.createdBy }}</p>
                        </div>
                        <div class="adjustment-summary">
                            <div class="summary-item">
                                <span>SKUs Applied:</span>
                                <strong>{{ qtyAdjustmentDetails.totalItems || qtyAdjustmentItems.length }}</strong>
                            </div>
                            <div class="summary-item">
                                <span>Cost Increase:</span>
                                <strong class="text-success">${{ qtyAdjustmentDetails.costIncrease }}</strong>
                            </div>
                            <div class="summary-item">
                                <span>Cost Decrease:</span>
                                <strong class="text-danger">${{ qtyAdjustmentDetails.costDecrease }}</strong>
                            </div>
                            <div class="summary-item">
                                <span>Net Change:</span>
                                <strong :class="qtyAdjustmentDetails.netChange >= 0 ? 'text-success' : 'text-danger'">
                                    ${{ qtyAdjustmentDetails.netChange }}
                                </strong>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="mb-3">
                    <h4>Adjusted Products</h4>
                    <CustomTable :noFixedWidth="true" style="font-size: 0.9em">
                        <template #head>
                            <tr>
                                <th>Product Name</th>
                                <th>Supplier SKU</th>
                                <th>Batch No</th>
                                <th>Location</th>
                                <th>Room</th>
                                <th>In Stock</th>
                                <th>Adjust By</th>
                                <th>New Qty</th>
                                <th>Unit</th>
                                <th>Reason Code</th>
                                <th>Notes</th>
                                <th>Adjusted By</th>
                            </tr>
                        </template>
                        <template #body>
                            <tr v-for="qtyAdjustmentItem of qtyAdjustmentItems" :key="qtyAdjustmentItem.id">
                                <td style="max-width: 130px">{{ qtyAdjustmentItem.item_name }}</td>
                                <td>{{ qtyAdjustmentItem.supplier_sku }}</td>
                                <td>{{ qtyAdjustmentItem.batch_no }}</td>
                                <td>{{ qtyAdjustmentItem.warehouse ? qtyAdjustmentItem.warehouse.name : "NA" }}</td>
                                <td>{{ qtyAdjustmentItem.is_front_room ? "Front Room" : "Back Room" }}</td>
                                <td>{{ qtyAdjustmentItem.stock_qty }}</td>
                                <td :class="qtyAdjustmentItem.adjust_qty >= 0 ? 'text-success' : 'text-danger'">
                                    {{ qtyAdjustmentItem.adjust_qty > 0 ? '+' : '' }}{{ qtyAdjustmentItem.adjust_qty }}
                                </td>
                                <td>{{ qtyAdjustmentItem.new_qty }}</td>
                                <td>Single Unit</td>
                                <td style="max-width: 80px">
                                    <span v-if="qtyAdjustmentItem.reasonCode" class="reason-badge" :title="qtyAdjustmentItem.reasonCode.description">
                                        {{ qtyAdjustmentItem.reasonCode.name }}
                                    </span>
                                    <span v-else>-</span>
                                </td>
                                <td>{{ qtyAdjustmentItem.notes || '-' }}</td>
                                <td>
                                    <span v-if="qtyAdjustmentItem.user">
                                        {{ getUserDisplayName(qtyAdjustmentItem.user) }}
                                    </span>
                                    <span v-else>-</span>
                                </td>
                            </tr>
                        </template>
                    </CustomTable>
                </div>
            </div>
        </AllowPermission>
    </Card>
</template>

<script>
import { mapState } from "vuex"
import CustomInput from "../../../components/crud/custom-input.vue"
import SelectDropdown from "../../../components/crud/SelectDropdown.vue"
import CustomTable from "../../../components/layout/CustomTable.vue"
import ProductSearchAndAdder from "../../../components/product/ProductSearchAndAdder.vue"
import ProductSearchInput from "../../../components/product/ProductSearchInput.vue"
import Button from "../../../components/UI/Button.vue"
import Card from "../../../components/UI/Card.vue"
import QuantityFetchApi from "../../../utils/fetch/quantity-fetch-api"
import { PERMISSION_CODES } from "../../../store/permission"
import AllowPermission from "../../../components/layout/AllowPermission.vue"
import { errMsg } from "../../../utils/common"
import BackNavigateBtn from "../../../components/UI/BackNavigateBtn.vue"
import InfoListItem from "../../../components/UI/InfoListItem.vue"
import SectionTitle from "../../../components/UI/SectionTitle.vue"
import { format } from "date-fns"

export default {
    data() {
        return {
            qtyAdjustmentDetails: null,
            qtyAdjustmentItems: [],
            loading: false,
            PERMISSION_CODES,
        }
    },
    components: {
        BackNavigateBtn,
        ProductSearchAndAdder,
        CustomInput,
        Card,
        Button,
        SelectDropdown,
        CustomTable,
        ProductSearchInput,
        AllowPermission,
        InfoListItem,
        SectionTitle,
    },
    async mounted() {
        await this.fetchQtyAdjustmentDetails()
    },
    computed: {
        ...mapState("misc", ["warehouses", "warehouseRooms"]),
    },
    methods: {
        async fetchQtyAdjustmentDetails() {
            try {
                this.loading = true
                const data = await QuantityFetchApi.getQuantityAdjustmentDetails(this.$route.params.id)
                
                // Set the parent adjustment details
                this.qtyAdjustmentDetails = data.result
                
                // Format the adjusted date if needed
                if (this.qtyAdjustmentDetails.createdAt && !this.qtyAdjustmentDetails.updatedAtFormatted1) {
                    this.qtyAdjustmentDetails.updatedAtFormatted1 = format(
                        new Date(this.qtyAdjustmentDetails.createdAt),
                        "MMM d, yyyy h:mm a"
                    );
                }
                
                // Set the adjustment items
                this.qtyAdjustmentItems = data.items || []
                
                // Fetch user information for each item if user_id is available
                await this.loadUserInformation()
                
            } catch (err) {
                console.error("Error fetching quantity adjustment details:", err)
                this.$toast.error("Failed to load quantity adjustment details")
            }
            this.loading = false
        },
        
        async loadUserInformation() {
            // Create a Set to store unique user IDs
            const userIds = new Set();
            
            // Collect all unique user IDs from items
            this.qtyAdjustmentItems.forEach(item => {
                if (item.user_id && !item.user) {
                    userIds.add(item.user_id);
                }
            });
            
            // If parent adjustment has user_id but no user object
            if (this.qtyAdjustmentDetails.user_id && !this.qtyAdjustmentDetails.user) {
                userIds.add(this.qtyAdjustmentDetails.user_id);
            }
            
            // Fetch user information for each unique user ID
            const userPromises = Array.from(userIds).map(async userId => {
                try {
                    const userData = await QuantityFetchApi.getUserInfo(userId);
                    return { userId, userData: userData.result };
                } catch (error) {
                    console.error(`Error fetching user info for ${userId}:`, error);
                    return { userId, userData: null };
                }
            });
            
            // Wait for all user data to be fetched
            const userResults = await Promise.all(userPromises);
            
            // Create a map of user IDs to user data
            const userMap = userResults.reduce((map, { userId, userData }) => {
                if (userData) {
                    map[userId] = userData;
                }
                return map;
            }, {});
            
            // Assign user data to adjustment items
            this.qtyAdjustmentItems = this.qtyAdjustmentItems.map(item => {
                if (item.user_id && userMap[item.user_id]) {
                    return { ...item, user: userMap[item.user_id] };
                }
                return item;
            });
            
            // Assign user data to parent adjustment if needed
            if (this.qtyAdjustmentDetails.user_id && userMap[this.qtyAdjustmentDetails.user_id]) {
                this.qtyAdjustmentDetails.user = userMap[this.qtyAdjustmentDetails.user_id];
                this.qtyAdjustmentDetails.createdBy = this.getUserDisplayName(userMap[this.qtyAdjustmentDetails.user_id]);
            }
        },
        
        getUserDisplayName(user) {
            if (!user) return '-';
            
            if (user.firstName && user.lastName) {
                return `${user.firstName} ${user.lastName}`;
            } else if (user.username) {
                return user.username;
            } else if (user.email) {
                return user.email;
            } else if (user.id) {
                return `User ID: ${user.id.substring(0, 8)}...`;
            }
            
            return 'Unknown user';
        }
    },
}
</script>

<style lang="scss" scoped>
@import "@/assets/variables.scss";
.qty-adjustment-setting {
    display: flex;
    width: 100%;
    align-items: center;
    gap: 15px;
}

.adjustment-header {
    background-color: $lightHoverColor;
    padding: 20px;
    border-radius: 8px;
    border: 1px solid $lightBorderColor;
    
    h3 {
        color: $primaryColor;
        font-weight: 600;
    }
}

.adjustment-summary {
    display: flex;
    flex-wrap: wrap;
    gap: 15px;
    margin-top: 10px;
    
    .summary-item {
        background-color: $lightCardBg;
        padding: 8px 15px;
        border-radius: 6px;
        box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        display: flex;
        align-items: center;
        border: 1px solid $lightBorderColor;
        
        span {
            color: rgba($textColor, 0.7);
            margin-right: 10px;
            font-size: 0.9rem;
        }
        
        strong {
            font-size: 1rem;
            color: $textColor;
            &.text-success {
                color: $successColor;
            }
            
            &.text-danger {
                color: $dangerColor;
            }
        }
    }
}

.reason-badge {
    display: inline-block;
    padding: 3px 8px;
    background-color: $lightHoverColor;
    border-radius: 4px;
    border: 1px solid $lightBorderColor;
    font-size: 0.85rem;
    color: $textColor;
    cursor: default;
}

/* Tokenized table paddings */
::v-deep table th,
::v-deep table td {
    padding: $tableCellPaddingY $tableCellPaddingX;
}

.dark .adjustment-header { background-color: lighten($darkCardBg, 4%); border-color: $darkBorderColor; h3 { color: $darkTextColor; } }
.dark .adjustment-summary .summary-item { background-color: $darkCardBg; border-color: $darkBorderColor; span { color: rgba($darkTextColor, 0.8); } strong { color: $darkTextColor; } strong.text-success { color: $successColorDark; } strong.text-danger { color: $dangerColorDark; } }
.dark .reason-badge { background-color: lighten($darkCardBg, 4%); border-color: $darkBorderColor; color: $darkTextColor; }

@media (max-width: 768px) {
    .adjustment-summary {
        margin-top: 20px;
        width: 100%;
    }
}
</style>
